import { IconListing } from "../store/store.ts";
export const NO_MOBILE =true
// Experimental, DISABLED: on new-project reset, create a genuinely fresh server
// session via configurator.initialize() instead of restoring the captured
// factory-default session blob. Confirmed broken: calling initialize() a SECOND
// time on an already-initialized Paramate.Configurator (as opposed to cold-boot's
// init(), which always builds a brand-new instance) corrupts something server-side
// — the SDK's own immediate follow-up calls inside initialize() (getParams,
// getObjectInfo) 401 on the freshly-created session, even with only one
// POST /configSessions observed (i.e. not a client-side double-call race).
// Reverted to the proven path: restoreSession() with the captured factory blob
// (see as-configurator handleResetConfigurator), a PUT /restore against the
// EXISTING session rather than a second initialize().
export const ENABLE_EXPERI_REINIT = false
export const MAX_STORED_NOTIFICATIONS = 50
export const MIN_WIDTH = 700
export const DEFAULT_SERVER_ROOT = 'https://apps.trinckle.com';
export const DEV_CDN_ROOT = 'https://hosted.trinckle.com/as-dev';
export const DEFAULT_ASSETS_PATH = `assets`;
export const DEFAULT_LANG_FILE_FOLDER_PATH = `data/lang`;

// window.location might be :
// https://hosted.trinckle.com/as-staging/apps/outlines/
// https://apps.trinckle.com/apps/outlines/
// https://localhost:1234

export const fullLocation = window.location.toString()
export const hostName = window.location.host.toString()
export const protocol = window.location.protocol // 'http:' or 'https:'

// ---------------------------------------------------------------------------
// Deployment tier — detected from hostname, immutable at runtime.
// 'local'   : localhost / private-network IPs
// 'staging' : any hosted environment that isn't production
// 'production': apps.trinckle.com (or window.fakeProduction for local testing)
// ---------------------------------------------------------------------------
export type DeploymentTier = 'local' |'dev'| 'staging' | 'production';

export const deploymentTier: DeploymentTier = (() => {
  // @ts-ignore
  if (window.fakeProduction || fullLocation.includes('apps.trinckle.com')) return 'production';
  const host = hostName.split(':')[0];
  if (['localhost', '127.0.0.1'].includes(host)
      || host.startsWith('192.168.') || host.startsWith('10.') || host.startsWith('172.'))
    return 'local';
  if(host.includes('as-staging')) return 'staging'
  return 'dev';
})();

// Backward-compat aliases used throughout the codebase
export const localDev     = deploymentTier === 'local';
export const isProduction = deploymentTier === 'production';

// ---------------------------------------------------------------------------
// Integration env — set explicitly via URL param or <as-app env="..."> attribute.
// Orthogonal to deploymentTier: you can be staging+grabcad, local+onprem, etc.
// No localStorage caching — env must be present on every load.
// ---------------------------------------------------------------------------
const getEnv = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const envParam = urlParams.get('env');
  if (envParam) return envParam;

  const asApp = document.querySelector('as-app');
  const attrParam = asApp?.getAttribute('env');
  if (attrParam) return attrParam;

  return null;
};

export const currentEnv = getEnv();
export const suppressExternalServices = currentEnv === 'onprem';

// True when running inside any iframe (e.g. as-code-demo srcdoc sandbox).
// window.location.protocol === 'about:' reliably identifies srcdoc/blank iframes
// regardless of sandbox allow-same-origin quirks that can make window.self === window.top.
export const inIframe = (() => {
  if (window.location.protocol === 'about:') return true;
  try { return window.self !== window.top; } catch { return true; }
})();

// True when external API calls should be suppressed (e.g. as-code-demo sandbox sets window.noAPI = true)
export const noAPI: boolean = (() => {
  if ((window as any).noAPI) return true;
  try { return window.parent !== window && !!(window as any).parent?.noAPI; } catch { return false; }
})();



export const useLocalAssets = !!document.querySelector('as-app[use-local-assets]');


// Get first path segment (e.g., 'as-staging' from '/as-staging/apps/...')
// Excludes file-name segments (anything with a dot, like 'tutorial.html')
export const pathNameParts = window.location.pathname.split("/").filter(x => x.length && !x.includes('.'));
// In an iframe (e.g. as-code-demo srcdoc) the pathname may be 'srcdoc' from about:srcdoc — skip it
export const appSuiteRootPath = (isProduction || inIframe) ? '' : (pathNameParts[0] || '');

// Full base URL with protocol and optional path prefix
export const fullAppSuiteRootURL = appSuiteRootPath
  ? `${protocol}//${hostName}/${appSuiteRootPath}`
  : `${protocol}//${hostName}`;

// API endpoint
export const apiRoot = (localDev && currentEnv !== 'onprem')
  ? 'https://hosted.trinckle.com/as-staging/user/api.php'
  : appSuiteRootPath
    ? `/${appSuiteRootPath}/user/api.php`
    : '/user/api.php';



export const DEFAULT_PARAMATE_API_HUB = 'https://api.paramate.trinckle.com';



export const DEFAULT_ICON_LIST:IconListing = {
  'toggle-workpiece-view': {
    default: 'features/viz-eye-open-blk.svg',
    hidden: 'features/viz-eye-closed-blk.svg'
  },
  'user-options': {
    default: 'user.png',
  },
  'transfer-mesh': {
    default: 'features/next-b.svg'
  },
  'help': {
    default: 'features/question-2.svg'
  },
  'info': {
    default: 'features/info.svg'
  },
  'close': {
    default: 'icons/close.svg'
  },
  'settings': {
    default: 'icons/gear.svg'
  }
}

export const serverCodes = {
  "ERRORCODE_NO_ERROR": -1,
  "ERRORCODE_UNKNOWN_ERROR": 0,
  "ERRORCODE_WRONG_CREDENTIALS": 1,
  "ERRORCODE_NOT_ACTIVE": 2,
  "ERRORCODE_WEAK_PASSWORD": 3,
  "ERRORCODE_PASSWORD_NOT_CONFIRMED": 4,
  "ERRORCODE_ALREADY_EXISTS": 5,
  "ERRORCODE_NOT_ALLOWED": 6,
  "ERRORCODE_NO_ACCESS": 7,
  "ERRORCODE_NOT_FOUND": 8,
  "ERRORCODE_INVALID": 9,
  "ERRORCODE_CAPTCHA": 101,
  "ERRORCODE_EMAIL": 102,

  "-1": "ERRORCODE_NO_ERROR",
  "0": "ERRORCODE_UNKNOWN_ERROR",
  "1": "ERRORCODE_WRONG_CREDENTIALS",
  "2": "ERRORCODE_NOT_ACTIVE",
  "3": "ERRORCODE_WEAK_PASSWORD",
  "4": "ERRORCODE_PASSWORD_NOT_CONFIRMED",
  "5": "ERRORCODE_ALREADY_EXISTS",
  "6": "ERRORCODE_NOT_ALLOWED",
  "7": "ERRORCODE_NO_ACCESS",
  "8": "ERRORCODE_NOT_FOUND",
  "9": "ERRORCODE_INVALID",
  "101": "ERRORCODE_CAPTCHA",
  "102": "ERRORCODE_EMAIL",
};
